import React, {FC, useContext} from 'react';

import {FormContext} from './context/FormContext';
import {FormProvider} from './context';
import {
  FormConfig,
  FormContextType,
  FormLayout,
  FormOnSubmit,
  FormStructure,
  FormValidators,
  FormValuesConstraint,
  HandleNext,
  NavigationControllers,
  OnBeforeNext,
  OnBeforeNextCallback,
  StructureProp,
} from './types';

/**
 * JIT package:
 * this module is designed for compile-time type inference and contract shaping,
 * with minimal runtime logic.
 */

/**
 * Re-export form-builder contract types so consumers import everything from
 * `@zoefin/form-builder` instead of depending on internal file paths.
 */
export type {
  FormConfig,
  FormContextType,
  FormLayout,
  FormOnSubmit,
  FormStructure,
  FormValidators,
  HandleNext,
  NavigationControllers,
  OnBeforeNext,
  OnBeforeNextCallback,
  StructureProp,
};

/**
 * Public helper type used by consumers to preserve structure literal inference.
 */
export type StructureFactory<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
> = <U extends StructureProp<T, S>>(structure: U) => U;

/**
 * Public helper type used by consumers to preserve navigation-controller literal
 * inference.
 *
 * The identity signature is generic over the argument for the same reason
 * `StructureFactory` is: it returns `C`, the literal type of the object passed
 * in, so `controllers.SomeStep` stays statically known at the call site.
 * Returning `NavigationControllers<T, S, K>` did not — that type is keyed by
 * `GetStepsWithNext<T, S>`, which collapses to `unknown` for a grouped
 * structure whose steps nest `next` (every real consumer). The resulting object
 * has no known keys, which is why callers were forced to cast to `any` to reach
 * a controller. Preserving `C` fixes the call sites; the collapse inside
 * `GetStepsWithNext` is a separate bug and is still there.
 */
export type NavigationControllersFactory<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K,
> = <C extends NavigationControllers<T, S, K>>(controllers: C) => C;

/**
 * Public API returned by `formBuilder`.
 * The identity factories are intentional: they keep strong generic inference
 * when consumers define callbacks/config in separate files.
 */
export interface FormBuilderApi<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K = undefined,
> {
  Form: FC<FormConfig<T, S, K>>;
  useFormContext: () => FormContextType<T, S, K>;
  stepFactoryHOC: (
    Step: React.FC<{ctx: FormContextType<T, S, K>}>,
  ) => () => React.JSX.Element;
  structureFactory: StructureFactory<T, S>;
  layoutFactory: (cb: FormLayout<T, S, K>) => FormLayout<T, S, K>;
  validatorsFactory: (
    validators: FormValidators<T, S, K>,
  ) => FormValidators<T, S, K>;
  navigationControllersFactory: NavigationControllersFactory<T, S, K>;
  handleNextFactory: (cb: HandleNext<T, S, K>) => HandleNext<T, S, K>;
  onBeforeNextFactory: (
    handlers: OnBeforeNext<T, S, K>,
  ) => OnBeforeNext<T, S, K>;
  onBeforeNextCallbackFactory: (
    cb: OnBeforeNextCallback<T, S, K>,
  ) => OnBeforeNextCallback<T, S, K>;
  onSubmitFactory: (cb: FormOnSubmit<T, S, K>) => FormOnSubmit<T, S, K>;
}

/**
 * Factory that builds a typed form API for a specific data/structure shape.
 */
export function formBuilder<
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
  K = undefined,
>(): FormBuilderApi<T, S, K> {
  const Form: FC<FormConfig<T, S, K>> = (props) => {
    return <FormProvider<T, S, K> {...props} />;
  };

  function useFormContext(): FormContextType<T, S, K> {
    return useContext(FormContext) as FormContextType<T, S, K>;
  }

  function stepFactoryHOC(Step: React.FC<{ctx: FormContextType<T, S, K>}>) {
    return function WrappedStep() {
      const context = useFormContext();

      return <Step ctx={context} />;
    };
  }

  const structureFactory: StructureFactory<T, S> = (structure) => structure;

  function layoutFactory(cb: FormLayout<T, S, K>): FormLayout<T, S, K> {
    return cb;
  }

  function validatorsFactory(
    validators: FormValidators<T, S, K>,
  ): FormValidators<T, S, K> {
    return validators;
  }

  const navigationControllersFactory: NavigationControllersFactory<T, S, K> = (
    controllers,
  ) => controllers;

  function handleNextFactory(cb: HandleNext<T, S, K>): HandleNext<T, S, K> {
    return cb;
  }

  function onBeforeNextFactory(
    handlers: OnBeforeNext<T, S, K>,
  ): OnBeforeNext<T, S, K> {
    return handlers;
  }

  function onBeforeNextCallbackFactory(
    cb: OnBeforeNextCallback<T, S, K>,
  ): OnBeforeNextCallback<T, S, K> {
    return cb;
  }

  function onSubmitFactory(cb: FormOnSubmit<T, S, K>): FormOnSubmit<T, S, K> {
    return cb;
  }

  return {
    Form,
    useFormContext,
    stepFactoryHOC,
    structureFactory,
    layoutFactory,
    validatorsFactory,
    navigationControllersFactory,
    handleNextFactory,
    onBeforeNextFactory,
    onBeforeNextCallbackFactory,
    onSubmitFactory,
  };
}
